import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import * as React from 'react';
import Colors, { Color } from '~/components/shared/Colors';
import Sizes from '~/components/shared/Sizes';
import type { Attribute, Color as TColor } from '~/services/api-utils.server';
import type { Product } from '~/services/api.server';
import { typedBoolean } from '~/utils/common';

export function Attributes({
  availableAttributes,
  selection,
  onAttributeChange,
  unavailableVariants,
  unavailableInventoryParts,
}: {
  availableAttributes: Product['available_attributes'];
  selection: { [k: string]: string };
  onAttributeChange: (attribute: string, value: string, newAttributes?: Record<string, string>) => void;
  unavailableVariants?: string[];
  unavailableInventoryParts: string[];
}) {
  const unavailableAttributes = React.useMemo(() => {
    return getUnavailableAttributes({ availableAttributes, selection, unavailableVariants });
  }, [availableAttributes, selection, unavailableVariants]);

  const handleChangeAttribute = (attribute: string, value: string) => {
    const isUnavailable = unavailableAttributes[attribute]?.includes(value);
    if (isUnavailable) {
      const validVariant = getValidVariant(availableAttributes, unavailableVariants ?? [], attribute, value);
      if (validVariant) {
        onAttributeChange(attribute, value, validVariant);
      }
    } else {
      onAttributeChange(attribute, value);
    }
  };

  return (
    <Stack gap={2}>
      {availableAttributes['colors'] && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Colors
            colors={availableAttributes['colors'] as TColor[]}
            selectedColor={selection['color']}
            onColorChange={(e) => handleChangeAttribute('color', e.currentTarget.value)}
            unavailableColors={unavailableAttributes['color'] ?? []}
            unavailableInventoryParts={unavailableInventoryParts}
          />
        </Stack>
      )}
      {availableAttributes['sizes'] && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Sizes
            sizes={availableAttributes['sizes'] as TColor[]}
            selectedSize={selection['size']}
            onSizeChange={(e) => handleChangeAttribute('size', e.currentTarget.value)}
            unavailableSizes={unavailableAttributes['size'] ?? []}
          />
        </Stack>
      )}
      {availableAttributes['book_types'] && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <BookTypes
            types={availableAttributes['book_types']}
            selectedType={selection['booktype']}
            onTypeChange={(e) => handleChangeAttribute('booktype', e.currentTarget.value)}
            unavailableTypes={unavailableAttributes['booktype'] ?? []}
          />
        </Stack>
      )}
    </Stack>
  );
}

export function AttributesStep({
  availableAttributes,
  selection,
  onAttributeChange,
  unavailableVariants,
  colorStep,
  unavailableInventoryParts,
}: {
  availableAttributes: Product['available_attributes'];
  selection: { [k: string]: string };
  onAttributeChange: (attribute: string, value: string, newAttributes?: Record<string, string>) => void;
  unavailableVariants?: string[];
  colorStep: boolean;
  unavailableInventoryParts: string[];
}) {
  const unavailableAttributes = React.useMemo(() => {
    return getUnavailableAttributes({ availableAttributes, selection, unavailableVariants });
  }, [availableAttributes, selection, unavailableVariants]);

  const handleChangeAttribute = (attribute: string, value: string) => {
    const isUnavailable = unavailableAttributes[attribute]?.includes(value);
    if (isUnavailable) {
      const validVariant = getValidVariant(availableAttributes, unavailableVariants ?? [], attribute, value);
      if (validVariant) {
        onAttributeChange(attribute, value, validVariant);
      }
    } else {
      onAttributeChange(attribute, value);
    }
  };

  const unavailableSize = unavailableAttributes['size'] ?? [];
  const sizes = availableAttributes['sizes']?.filter((size) => !unavailableSize?.includes(size?.name));
  const selectedColor = availableAttributes['colors']?.find((color) => color.name === selection['color']) as TColor;
  const unavailableSelectedColorInventory = unavailableInventoryParts?.filter((variant) =>
    variant.includes(selectedColor?.name)
  );

  return (
    <Stack gap={2}>
      {availableAttributes['colors'] && (
        <Stack direction="row" alignItems="center" spacing={1}>
          {colorStep && availableAttributes['colors'].length > 1 ? (
            <Colors
              colors={availableAttributes['colors'] as TColor[]}
              selectedColor={selection['color']}
              onColorChange={(e) => handleChangeAttribute('color', e.currentTarget.value)}
              unavailableColors={unavailableAttributes['color'] ?? []}
              unavailableInventoryParts={unavailableInventoryParts}
            />
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" color="text.secondary" alignSelf="baseline" mr={1.5}>
                Color:
              </Typography>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ '.MuiBox-root': { mr: 0 } }}>
                <Color
                  color={selectedColor}
                  readOnly
                  noStockAvailable={Boolean(unavailableSelectedColorInventory?.length > 0)}
                  disabledLabel={
                    <ColorTooltipText
                      colorName={selectedColor?.name}
                      unavailableInventory={unavailableSelectedColorInventory}
                    />
                  }
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
      {availableAttributes['sizes'] && (
        <Stack direction="row" alignItems="center" spacing={1}>
          {colorStep ? (
            <ReadOnlySize sizes={sizes} />
          ) : (
            <Sizes
              sizes={availableAttributes['sizes'] as TColor[]}
              selectedSize={selection['size']}
              onSizeChange={(e) => handleChangeAttribute('size', e.currentTarget.value)}
              unavailableSizes={unavailableAttributes['size'] ?? []}
            />
          )}
        </Stack>
      )}
      {availableAttributes['book_types'] && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <BookTypes
            types={availableAttributes['book_types']}
            selectedType={selection['booktype']}
            onTypeChange={(e) => handleChangeAttribute('booktype', e.currentTarget.value)}
            unavailableTypes={unavailableAttributes['booktype'] ?? []}
          />
        </Stack>
      )}
    </Stack>
  );
}

type TAttributes = {
  availableAttributes: Product['available_attributes'];
  selection: { [k: string]: string };
  unavailableVariants?: string[];
};

type AttributesProps = TAttributes & {
  unavailable_inventory_parts?: string[];
  unavailableInventoryParts: string[];
};

export function ReadOnlyAttributes({
  availableAttributes,
  selection,
  unavailableVariants,
  unavailableInventoryParts,
}: AttributesProps) {
  const unavailableAttributes = React.useMemo(() => {
    return getUnavailableAttributes({ availableAttributes, selection, unavailableVariants });
  }, [availableAttributes, selection, unavailableVariants]);

  const colors = availableAttributes['colors'] as TColor[];
  const sizes = availableAttributes['sizes'];
  const availableBookTypes = availableAttributes['book_types'];
  const unavailableBookTypes = unavailableAttributes['booktype'] ?? [];

  return (
    <Stack gap={2}>
      {colors && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" color="text.secondary" alignSelf="baseline" mr={1.5}>
            {`Color${colors?.length > 1 ? 's' : ''}:`}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            sx={{ '.MuiBox-root': { mr: 0, position: 'relative' } }}
          >
            <>
              {colors?.map((color) => {
                const unavailableColorAttribute = unavailableVariants?.filter((variant) =>
                  variant.includes(color.name)
                );
                const unavailableColorInventory = unavailableInventoryParts?.filter((variant) =>
                  variant.includes(color.name)
                );
                if (unavailableColorAttribute?.length === 0 && unavailableColorInventory?.length === 0) {
                  return <Color key={color.hex} color={color} readOnly />;
                }

                return (
                  <Color
                    key={color.hex}
                    color={color}
                    disabled
                    isUnavailable={(unavailableColorAttribute?.length ?? 0) > 0}
                    noStockAvailable={(unavailableColorInventory?.length ?? 0) > 0}
                    disabledLabel={
                      <ColorTooltipText
                        colorName={color.name}
                        unavailableAttribute={unavailableColorAttribute}
                        unavailableInventory={unavailableColorInventory}
                      />
                    }
                    readOnly
                  />
                );
              })}
            </>
          </Stack>
        </Stack>
      )}
      {sizes && sizes.length > 0 && <ReadOnlySize sizes={sizes} />}
      {availableBookTypes && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" pt={0.5} color="text.secondary">
            Types:
          </Typography>
          <Stack direction="row" alignItems="center" flexWrap="wrap">
            {availableBookTypes.map((type: Attribute, index) => {
              const isUnavailable = unavailableBookTypes.includes(type.name);

              return (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  sx={{
                    px: 1,
                    py: 0.25,
                    minHeight: 0,
                    minWidth: 48,
                    mr: 1,
                    mb: 1,
                    borderStyle: isUnavailable ? 'dashed' : 'solid',
                    '&:hover': { borderStyle: isUnavailable ? 'dashed' : 'solid' },
                  }}
                  name="type"
                  value={type.name}
                >
                  {type.name}
                </Button>
              );
            })}
          </Stack>
        </Stack>
      )}
      {unavailableVariants && unavailableVariants?.length > 0 && (
        <Alert severity="info" sx={{ '.MuiAlert-message': { fontSize: 16, fontWeight: 500 } }}>
          Some variants are not available
        </Alert>
      )}
    </Stack>
  );
}

export function ColorTooltipText({
  colorName,
  unavailableAttribute,
  unavailableInventory,
}: {
  colorName: string;
  unavailableAttribute?: string[];
  unavailableInventory?: string[];
}) {
  const unavailableSizes = unavailableAttribute?.map((attribute) => attribute.split(',')[1])?.join('/');
  const unavailableStock = unavailableInventory?.map((attribute) => attribute.split(',')[1])?.join('/');
  return (
    <Box textAlign="center">
      {colorName}
      {unavailableAttribute && unavailableAttribute?.length > 0 && (
        <>
          <br />
          {`Not available for size${unavailableAttribute?.length > 1 ? 's' : ''} ${unavailableSizes}`}
        </>
      )}
      {unavailableInventory && unavailableInventory?.length > 0 && (
        <>
          <br />
          {!unavailableStock
            ? 'No stock available'
            : `No stock available ${unavailableInventory?.length > 1 ? 'for sizes' : 'for size'} ${unavailableStock}`}
        </>
      )}
    </Box>
  );
}

export function ReadOnlySize({ sizes }: { sizes: Attribute[] }) {
  return (
    <Typography
      variant="subtitle2"
      color="text.secondary"
      mr={{ xs: 1, md: 0 }}
      alignItems="center"
      alignSelf="flex-start"
    >
      Sizes ({sizes?.map((size) => size.name).join(', ')}) will be available to select on checkout.
    </Typography>
  );
}

function getUnavailableAttributes({ availableAttributes, selection, unavailableVariants }: TAttributes) {
  if (!unavailableVariants || Object.keys(availableAttributes).length < 2) {
    return {};
  }

  const _unavailableAttributes: { [k: string]: string[] } = {};
  const singularAttrKeys = Object.keys(availableAttributes).map((key) =>
    key === 'book_types' ? 'booktype' : key.slice(0, -1)
  );

  for (let i = 0; i < singularAttrKeys.length; i++) {
    const attrKey = singularAttrKeys[i];
    const restAttrKeys = singularAttrKeys.slice(0, i).concat(singularAttrKeys.slice(i + 1));
    const selectedAttributes = [] as string[];
    restAttrKeys.forEach((key) => {
      if (selection[key]) {
        selectedAttributes.push(selection[key]);
      }
    });

    _unavailableAttributes[attrKey] = unavailableVariants
      .map((variant) => {
        const attributes = variant.split(',');
        const differentAttributes = _.xor(selectedAttributes, attributes);
        return differentAttributes.length === 1 ? differentAttributes[0] : null;
      })
      .filter(typedBoolean);
  }

  return _unavailableAttributes;
}

function BookTypes({
  types,
  selectedType,
  onTypeChange,
  unavailableTypes,
}: {
  types: Attribute[];
  selectedType: string;
  onTypeChange: React.MouseEventHandler<HTMLButtonElement>;
  unavailableTypes: string[];
}) {
  return (
    <>
      <Typography variant="subtitle2" pt={0.5} color="text.secondary">
        Types:
      </Typography>
      <Stack direction="row" alignItems="center" flexWrap="wrap">
        {types.map((type: Attribute, index) => {
          const isUnavailable = unavailableTypes.includes(type.name);
          const selectedColor =
            selectedType === type.name ? 'text.primary' : isUnavailable ? 'text.disabled' : 'primary.main';

          return (
            <Button
              key={index}
              variant="outlined"
              size="small"
              sx={{
                px: 1,
                py: 0.25,
                minHeight: 0,
                minWidth: 48,
                mr: 1,
                mb: 1,
                color: selectedColor,
                borderColor: selectedColor,
                borderStyle: isUnavailable ? 'dashed' : 'solid',
                '&:hover': {
                  color: selectedColor,
                  borderColor: selectedColor,
                  borderStyle: isUnavailable ? 'dashed' : 'solid',
                },
              }}
              name="type"
              value={type.name}
              onClick={onTypeChange}
            >
              {type.name}
            </Button>
          );
        })}
      </Stack>
    </>
  );
}

function getValidVariant(
  availableAttributes: Product['available_attributes'],
  unavailableVariants: string[],
  attributeName: string,
  attributeValue: string
): { [k: string]: string } | null {
  const pluralAttr = attributeName === 'booktype' ? 'book_types' : `${attributeName}s`;
  const keyIndex = Object.keys(availableAttributes).indexOf(pluralAttr);

  const keys = Object.keys(availableAttributes).filter((key, i) => i !== keyIndex);
  const keySizes = keys
    .map((key) => availableAttributes[key as keyof typeof availableAttributes]?.length)
    .filter(typedBoolean);
  const size = keySizes.reduce((acc, cur) => acc * cur, 1);
  let i = 0;

  while (i < size) {
    const perm: { [k: string]: { name: string } } = {};
    let j = i;
    keys.forEach((key, idx) => {
      const attr = availableAttributes[key as keyof typeof availableAttributes]?.[j % keySizes[idx]];
      if (attr) {
        perm[key] = attr;
      }
      j = Math.floor(j / keySizes[idx]);
    });

    const variant = Object.values(perm).map((value) => value.name);
    variant.splice(keyIndex, 0, attributeValue);
    if (!unavailableVariants.includes(variant.join(','))) {
      const entries = Object.entries(perm).map(([key, value]) => [getSingularAttributeName(key), value.name]);
      entries.splice(keyIndex, 0, [attributeName, attributeValue]);
      return Object.fromEntries(entries);
    }

    i++;
  }

  return null;
}

function getSingularAttributeName(attributeName: string) {
  if (attributeName.endsWith('s')) {
    return attributeName === 'book_types' ? 'booktype' : attributeName.slice(0, -1);
  }
  return attributeName;
}
